// ----------------------------------------------------------------------
// <copyright file="Izenda.RegressionLineSettings.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import ChartSettingsContainer from './Izenda.ChartSettingsContainer';
import { CHART_SETTINGS } from 'logic/data/Izenda.ReportPartChartConstants';
import localizer from 'share/localization/Izenda.Localizer';
import { every, findIndex } from 'lodash/fp';
import DashStyle from 'share/components/controls/Izenda.DashStyleDropdown';
import Thickness from 'share/components/controls/Izenda.Thinkness';
import { observer } from 'mobx-react';
import RegressionLineStore from 'logic/Izenda.RegressionLineStore';

const {
  REGRESSION_TYPE,
  REGRESSION_TYPE_OPTIONS,
  REGRESSION_POLYNOMIAL_ORDER,
  REGRESSION_LINEAR_DECIMAL_PLACES_MAX_VALUE,
  REGRESSION_LINEAR_DECIMAL_PLACES_MIN_VALUE,
  REGRESSION_EXTRAPOLATE_MAX_VALUE,
  REGRESSION_EXTRAPOLATE_MIN_VALUE
} = CHART_SETTINGS;

function getSchema(fields: Array<any>, orderTooltip: string) {
  return {
    fields: {
      schemaKey: 'REGRESSION_LINE_SCHEMA',
      field: {
        title: localizer.getText('REGRESSION_LINE_FIELD_SELECT'),
        infoIcon: localizer.getText('REGRESSION_LINE_FIELD_SELECT_TOOLTIP'),
        type: 'select',
        fnValue: 'getField',
        props: {
          options: fields
        }
      },
      visible: {
        title: localizer.getText('COMMON_LABEL_VISIBLE'),
        type: 'checkbox',
        fnValue: 'getVisible'
      },
      hideInLegend: {
        title: localizer.getText('REGRESSION_LINE_HIDE_IN_LEGEND'),
        type: 'checkbox',
        fnValue: 'getHideInLegend',
        isHidden: 'isHiddenHideInLegend'
      },
      name: {
        title: localizer.getText('COMMON_LABEL_NAME'),
        infoIcon: localizer.getText('REGRESSION_LINE_NAME_TOOLTIP'),
        type: 'text',
        fnValue: 'getName'
      },
      type: {
        title: localizer.getText('REGRESSION_LINE_TYPE'),
        type: 'select',
        fnValue: 'getType',
        props: {
          options: REGRESSION_TYPE_OPTIONS
        }
      },
      decimalPlaces: {
        title: localizer.getText('REGRESSION_LINE_DECIMAL_PLACES'),
        infoIcon: localizer.getText('REGRESSION_LINE_DECIMAL_PLACES_TOOLTIP'),
        type: 'number',
        fnValue: 'getDecimalPlaces',
        props: {
          max: REGRESSION_LINEAR_DECIMAL_PLACES_MAX_VALUE,
          min: REGRESSION_LINEAR_DECIMAL_PLACES_MIN_VALUE
        }
      },
      order: {
        title: localizer.getText('REGRESSION_LINE_ORDER'),
        isHidden: 'isHiddenOrder',
        type: 'select',
        fnValue: 'getOrder',
        infoIcon: orderTooltip,
        props: {
          options: REGRESSION_POLYNOMIAL_ORDER
        }
      },
      color: {
        title: localizer.getText('COMMON_LABEL_COLOR'),
        type: 'color',
        fnValue: 'getColor'
      },
      dashStyle: {
        title: localizer.getText('BORDER_SETTING_LABEL_DASH_STYLE'),
        component: DashStyle,
        fnValue: 'getDashStyle'
      },
      lineWidth: {
        title: localizer.getText('BORDER_SETTING_LABEL_THICKNESS'),
        component: Thickness,
        fnValue: 'getLineWidth'
      },
      extrapolate: {
        title: localizer.getText('REGRESSION_LINE_EXTRAPOLATE'),
        type: 'number',
        infoIcon: localizer.getText('REGRESSION_LINE_EXTRAPOLATE_TOOLTIP'),
        isHidden: 'isHiddenExtrapolate',
        fnValue: 'getExtrapolate',
        props: {
          max: REGRESSION_EXTRAPOLATE_MAX_VALUE,
          min: REGRESSION_EXTRAPOLATE_MIN_VALUE
        }
      }
    }
  };
}

@observer
export default class RegressionLineSettings extends Component {
  constructor(props: any) {
    super(props);
    this.store = new RegressionLineStore(props.reportPartDetails);
  }

  componentWillUnmount() {
    this.store.disposeDataObserver();
  }
  /**
   * On closing popup
   */
  getValue() {
    const {
      reportPartDetails: { reportPartContent }
    } = this.props;
    this.store.updateElements(reportPartContent);
  }

  /**
   * On change data in schema
   * @param {any} schemaData
   * @param {string} changedKey
   * @param {Array<string>} keyPath
   * @param {any} newVal
   */
  onChangeValue(schemaData: any, changedKey: any, keyPath: Array<string>, newVal: any) {
    schemaData[changedKey].value = newVal;
    const {
      field: { value: fieldName }
    } = schemaData;
    const { store } = this;
    const fieldIndex = findIndex({ name: fieldName }, store.serieElements);
    const field = fieldIndex >= 0 ? store.serieElements[fieldIndex] : null;
    if (field) {
      if (changedKey === 'field') {
        store.setConfigField(field);
      } else if (store[changedKey] !== schemaData[changedKey].value) {
        // Avoid unneccessary re-render chart
        switch (changedKey) {
          case 'name':
            store.changeName(field, schemaData);
            break;
          case 'decimalPlaces':
            store.changeDecimalPlaces(field, schemaData);
            break;
          case 'extrapolate':
            store.changeExtrapolate(field, schemaData);
            break;
          default:
            store.updateValue(changedKey, field, schemaData);
        }
      }
    }
  }

  /**
   * Get configuration definition
   */
  getConfigurationDef() {
    const {
      store,
      store: { model }
    } = this;

    return {
      definedSchema: getSchema(store.buildFieldOptions(), store.orderTooltip),
      source: {},
      mappingSource: {
        getField: () => store.configField.fieldName,
        getVisible: () => model.visible,
        getHideInLegend: () => model.hideInLegend,
        getType: () => model.type,
        getOrder: () => model.order,
        isHiddenHideInLegend: () => !model.visible,
        isHiddenOrder: () => model.type !== REGRESSION_TYPE.polynomial,
        isHiddenExtrapolate: () => {
          const currentType = model.type;
          return every(regressionType => currentType !== regressionType, [
            REGRESSION_TYPE.polynomial,
            REGRESSION_TYPE.linear
          ]);
        },
        getLineWidth: () => model.lineWidth,
        getColor: () => model.color,
        getDashStyle: () => model.dashStyle,
        getDecimalPlaces: () => model.decimalPlaces,
        getExtrapolate: () => model.extrapolate,
        getName: () => model.name
      },
      onChangeValue: this.onChangeValue.bind(this)
    };
  }

  render() {
    const {
      reportPartType,
      reportPartDetails,
      reportPartDetails: { reportPartContent }
    } = this.props;
    const { store } = this;
    const allProps = this.props.allChartProps();
    allProps.seriesOptions = store.valuesElements
      .concat(store.valuesLabelsElements)
      .concat(reportPartContent.labelsElements)
      .concat(reportPartContent.separatorsElements)
      .concat(reportPartContent.bubbleSizeElements);

    return (
      <ChartSettingsContainer
        className="regression-line-settings"
        isLoading={reportPartDetails.isLoadingData}
        reportPart={reportPartDetails}
        type={reportPartType}
        configurationDef={this.getConfigurationDef()}
        allChartProps={allProps}
        chartData={store.getChartData(reportPartContent)}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartRegressionLineSettings.js